import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Gift, Plus, Sparkles, Calendar, Users, ExternalLink,
} from "lucide-react";

const STATUS_BADGE = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-700" },
  pending_approval: { label: "Pending Approval", cls: "bg-amber-100 text-amber-800" },
  approved: { label: "Approved", cls: "bg-blue-100 text-blue-700" },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-700" },
  distributed: { label: "Distributed", cls: "bg-emerald-100 text-emerald-700" },
};

const fmtINR = (n) => {
  if (n === null || n === undefined || isNaN(Number(n))) return "—";
  return "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });
};

export default function FestivalsList() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [festivals, setFestivals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", year: currentYear, festival_date: "", description: "", total_budget: 0 });
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [{ data: fests }, { data: sum }] = await Promise.all([
        api.get(`/festivals?year=${year}`),
        api.get(`/festivals-budget/summary?year=${year}`),
      ]);
      setFestivals(fests || []);
      setSummary(sum);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [year]);

  const seedPredefined = async () => {
    if (!window.confirm(`Seed predefined festivals (Diwali, Holi, Eid, Christmas, New Year, Independence Day) for ${year}?`)) return;
    try {
      const { data } = await api.post(`/festivals/seed-predefined?year=${year}`);
      toast.success(`Seeded ${data.created.length} festivals (${data.skipped.length} already existed)`);
      refresh();
    } catch (e) {
      toast.error("Seed failed");
    }
  };

  const create = async () => {
    if (!form.name || form.name.length < 2) return toast.error("Name required");
    if (!form.festival_date) return toast.error("Date required");
    setCreating(true);
    try {
      await api.post("/festivals", {
        ...form,
        year: parseInt(year),
        total_budget: parseFloat(form.total_budget) || 0,
        tier_cash_amounts: { A: 0, B: 0, C: 0, D: 0 },
        tier_voucher_amounts: { A: 0, B: 0, C: 0, D: 0 },
      });
      toast.success(`Festival "${form.name}" created`);
      setShowCreate(false);
      setForm({ name: "", year: currentYear, festival_date: "", description: "", total_budget: 0 });
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Create failed");
    } finally { setCreating(false); }
  };

  const years = useMemo(() => {
    const arr = [];
    for (let y = currentYear + 1; y >= currentYear - 3; y--) arr.push(y);
    return arr;
  }, [currentYear]);

  return (
    <div className="space-y-4" data-testid="festivals-list">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
            <Gift className="h-3 w-3" /> HR Tools
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Festival & Gift Distribution
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Manage company-wide festival gifts across 4 employee tiers. HR drafts → Senior Mgmt approves → HR distributes.
          </p>
        </div>
        <Button asChild variant="outline" size="sm" data-testid="tiers-btn">
          <Link to="/admin/festival-tiers"><Users className="h-3.5 w-3.5 mr-1.5" /> Manage Tiers</Link>
        </Button>
        <Button variant="outline" size="sm" onClick={seedPredefined} data-testid="seed-btn">
          <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Seed Predefined
        </Button>
        <Button onClick={() => setShowCreate(true)} className="brand-bg brand-bg-hover text-white" size="sm" data-testid="new-festival-btn">
          <Plus className="h-3.5 w-3.5 mr-1.5" /> New Festival
        </Button>
      </div>

      {/* Year filter + budget summary */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-end gap-4">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Year</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-28 mt-1" data-testid="year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {summary && (
          <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-slate-50 border border-slate-200 rounded p-2.5">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Festivals</div>
              <div className="text-xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }} data-testid="stat-count">{summary.festival_count}</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-2.5">
              <div className="text-[10px] uppercase tracking-widest text-blue-700">Total Budget</div>
              <div className="text-xl font-bold text-blue-700" style={{ fontFamily: "Chivo" }} data-testid="stat-budget">{fmtINR(summary.total_budget)}</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded p-2.5">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700">Actual Spend</div>
              <div className="text-xl font-bold text-emerald-700" style={{ fontFamily: "Chivo" }} data-testid="stat-actual">{fmtINR(summary.total_actual_spend)}</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded p-2.5">
              <div className="text-[10px] uppercase tracking-widest text-amber-800">Remaining</div>
              <div className="text-xl font-bold text-amber-800" style={{ fontFamily: "Chivo" }} data-testid="stat-remaining">{fmtINR(summary.remaining_budget)}</div>
            </div>
          </div>
        )}
      </div>

      {/* Festivals grid */}
      {loading ? (
        <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
      ) : festivals.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-400">
          <Gift className="h-10 w-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No festivals for {year}.</p>
          <Button onClick={seedPredefined} variant="link" size="sm" className="mt-2">Seed predefined festivals →</Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="festival-cards">
          {festivals.map((f) => {
            const badge = STATUS_BADGE[f.status] || STATUS_BADGE.draft;
            return (
              <Link
                key={f.id}
                to={`/admin/festivals/${f.id}`}
                className="bg-white border border-slate-200 hover:border-red-400 hover:shadow-md transition rounded-lg p-4 block"
                data-testid={`festival-card-${f.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-slate-900 text-base truncate" style={{ fontFamily: "Chivo" }}>{f.name}</h3>
                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
                      <Calendar className="h-3 w-3" />
                      {f.festival_date}
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${badge.cls}`}>{badge.label}</span>
                </div>
                {f.description && (
                  <p className="text-xs text-slate-600 mt-2 italic line-clamp-2">{f.description}</p>
                )}
                <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-100 text-xs">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Budget</div>
                    <div className="font-bold text-slate-900">{fmtINR(f.total_budget)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Actual</div>
                    <div className="font-bold text-emerald-700">{fmtINR(f.total_actual_spend)}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {["A", "B", "C", "D"].map((t) => (
                    <span key={t} className="text-[9px] font-mono bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">
                      {t}: {fmtINR((f.tier_cash_amounts?.[t] || 0) + (f.tier_voucher_amounts?.[t] || 0))}
                    </span>
                  ))}
                </div>
                <div className="mt-3 text-right text-xs text-red-700 font-semibold flex items-center justify-end gap-1">
                  Open <ExternalLink className="h-3 w-3" />
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-dialog">
          <DialogHeader>
            <DialogTitle>New Festival</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Diwali Bonus 2026"
                  className="mt-1"
                  data-testid="name-input"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Date *</Label>
                <Input
                  type="date"
                  value={form.festival_date}
                  onChange={(e) => setForm({ ...form, festival_date: e.target.value })}
                  className="mt-1"
                  data-testid="date-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Total Budget (₹)</Label>
              <Input
                type="number"
                step="1000"
                value={form.total_budget}
                onChange={(e) => setForm({ ...form, total_budget: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={create} disabled={creating} className="brand-bg brand-bg-hover text-white" data-testid="create-btn">
              {creating ? "Creating…" : "Create Draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
